import {
  Box, Button, Flex, Input, Link, Select, Spinner, Table, Tbody, Td, Text, Th, Thead, Tr, Badge, VStack, useColorModeValue, Center, useBreakpointValue,
} from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FaSync } from 'react-icons/fa';
import { toast } from 'react-toastify';
import * as TaskService from 'services/TaskService';
import { Task, TaskStatus } from 'utils/interfaces';
import { buildMapsUrl } from 'utils/common-utils';
import EmployeeTasksCardComponent from './EmployeeTasksCard';

const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  DA_INIZIARE: 'Da iniziare',
  IN_CORSO: 'In corso',
  COMPLETATO: 'Completato',
};

const formatDateDDmmYYYY = (dateStr: string): string => {
  const d = new Date(dateStr);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const formatTime = (dateStr: string): string => {
  const d = new Date(dateStr);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const getTodayDateStr = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

type DateFilterMode = 'today' | 'all' | 'custom';
type StatusFilterMode = 'active' | 'completed' | 'all';

// Compiling a report navigates away to /interventi/:taskId/rapporto and back — this component
// unmounts in between, so plain useState would forget the filters. sessionStorage survives that
// unmount/remount (but not a real new tab/session), which is exactly the "torno indietro" case.
const FILTERS_STORAGE_KEY = 'employeeTasksFilters';

interface StoredFilters {
  dateMode: DateFilterMode;
  customDateFrom: string;
  customDateTo: string;
  statusMode: StatusFilterMode;
}

const DEFAULT_FILTERS: StoredFilters = { dateMode: 'today', customDateFrom: '', customDateTo: '', statusMode: 'active' };

const loadStoredFilters = (): StoredFilters => {
  try {
    const raw = sessionStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return DEFAULT_FILTERS;
    const parsed = JSON.parse(raw);
    return {
      dateMode: parsed.dateMode ?? DEFAULT_FILTERS.dateMode,
      customDateFrom: parsed.customDateFrom ?? DEFAULT_FILTERS.customDateFrom,
      customDateTo: parsed.customDateTo ?? DEFAULT_FILTERS.customDateTo,
      statusMode: parsed.statusMode ?? DEFAULT_FILTERS.statusMode,
    };
  } catch {
    return DEFAULT_FILTERS;
  }
};

export default function EmployeeTasksTable() {
  const { keycloak } = useKeycloak();
  const token = keycloak?.token;
  const history = useHistory();

  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageIndex, setPageIndex] = useState(0);

  const [dateMode, setDateMode] = useState<DateFilterMode>(() => loadStoredFilters().dateMode);
  const [customDateFrom, setCustomDateFrom] = useState(() => loadStoredFilters().customDateFrom);
  const [customDateTo, setCustomDateTo] = useState(() => loadStoredFilters().customDateTo);
  const [statusMode, setStatusMode] = useState<StatusFilterMode>(() => loadStoredFilters().statusMode);

  useEffect(() => {
    sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify({ dateMode, customDateFrom, customDateTo, statusMode }));
  }, [dateMode, customDateFrom, customDateTo, statusMode]);

  const isDefaultFilters = dateMode === 'today' && statusMode === 'active';
  const pageSize = dateMode === 'today' ? 100 : 10;

  const shadowBox = useColorModeValue('14px 17px 40px 4px rgba(112, 144, 176, 0.08)', 'none');
  const isMobile = useBreakpointValue({ base: true, md: true, lg: false });

  const fetchTasks = async () => {
    if (!token) return;
    setLoading(true);
    try {
      let dateFromParam: string | undefined;
      let dateToParam: string | undefined;
      if (dateMode === 'today') {
        const todayStr = getTodayDateStr();
        dateFromParam = todayStr;
        dateToParam = todayStr;
      } else if (dateMode === 'custom') {
        dateFromParam = customDateFrom || undefined;
        dateToParam = customDateTo || undefined;
      }

      const statusParam: TaskStatus | undefined = statusMode === 'completed' ? 'COMPLETATO' : undefined;

      const data = await TaskService.getMyTasks(
        token,
        () => keycloak?.logout(),
        {
          pageNumber: pageIndex,
          pageSize: pageSize,
          status: statusParam,
          dateFrom: dateFromParam,
          dateTo: dateToParam,
        }
      );
      if (data) {
        const content = statusMode === 'active'
          ? data.content.filter(t => t.status === 'DA_INIZIARE' || t.status === 'IN_CORSO')
          : data.content;
        setTasks(content);
      }
    } catch (err) {
      toast.error('Errore durante il caricamento dei task');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, pageIndex, dateMode, customDateFrom, customDateTo, statusMode]);

  useEffect(() => {
    setPageIndex(0);
  }, [dateMode, customDateFrom, customDateTo, statusMode]);

  // Only DA_INIZIARE -> IN_CORSO is a real status PATCH here. Completing an intervention just
  // navigates to the report form without flipping the task to COMPLETATO — otherwise abandoning that
  // form (crash, "Annulla", back button) would strand the task as COMPLETATO with no report and no
  // way back in. The task stays IN_CORSO until the report is actually submitted; the backend marks it
  // COMPLETATO as part of a successful report creation.
  const handleStatusChange = async (task: Task) => {
    if (!token || task.status === 'COMPLETATO') return;
    if (task.status === 'IN_CORSO') {
      history.push(`/dipendente/interventi/${task.id}/rapporto`);
      return;
    }
    try {
      // No effectiveStartDateTime here: the backend already defaults it to its own (timezone-correct)
      // LocalDateTime.now() when unset, same as effectiveEndDateTime on report submission. Sending
      // new Date().toISOString() (UTC) used to get deserialized straight into that LocalDateTime
      // field with no zone conversion, landing hours behind the real local time.
      const res = await TaskService.updateTaskStatus(
        token,
        () => keycloak?.logout(),
        task.id,
        { status: 'IN_CORSO' }
      );
      if (res) {
        toast.success('Stato aggiornato');
        fetchTasks();
      }
    } catch (err) {
      toast.error('Errore durante l\'aggiornamento dello stato');
    }
  };

  return (
    <Box>
      <VStack spacing={4} align="start" w="full">
        <Flex justify="space-between" align="center" w="full" px={{ base: 0, md: 4 }} >
          <Text fontSize="2xl" fontWeight="bold" color="#9FB50C">
            I miei Interventi
          </Text>
          <Button
            leftIcon={<FaSync />}
            onClick={fetchTasks}
            variant="ghost"
            colorScheme="blue"
            isLoading={loading}
          >
            Aggiorna
          </Button>
        </Flex>

        <Box w="full" bg="white" borderRadius="12px" boxShadow={shadowBox} p={{ base: 2, md: 4 }}>
          <Flex gap={3}>
            <Box flex="1" minW="0">
              <Text fontSize="sm" fontWeight="bold" mb={2}>Periodo</Text>
              <Select
                size="sm"
                value={dateMode}
                onChange={e => setDateMode(e.target.value as DateFilterMode)}
              >
                <option value="today">Solo oggi</option>
                <option value="all">Tutte le date</option>
                <option value="custom">Intervallo personalizzato</option>
              </Select>
            </Box>
            <Box flex="1" minW="0">
              <Text fontSize="sm" fontWeight="bold" mb={2}>Stato</Text>
              <Select
                size="sm"
                value={statusMode}
                onChange={e => setStatusMode(e.target.value as StatusFilterMode)}
              >
                <option value="active">Da iniziare e in corso</option>
                <option value="completed">Completati</option>
                <option value="all">Tutti</option>
              </Select>
            </Box>
          </Flex>
          {dateMode === 'custom' && (
            <Flex gap={3} mt={3}>
              <Box flex="1" minW="0">
                <Text fontSize="sm" fontWeight="bold" mb={2}>Data da</Text>
                <Input
                  type="date"
                  size="sm"
                  value={customDateFrom}
                  onChange={e => setCustomDateFrom(e.target.value)}
                />
              </Box>
              <Box flex="1" minW="0">
                <Text fontSize="sm" fontWeight="bold" mb={2}>Data a</Text>
                <Input
                  type="date"
                  size="sm"
                  value={customDateTo}
                  onChange={e => setCustomDateTo(e.target.value)}
                />
              </Box>
            </Flex>
          )}
          {isDefaultFilters && (
            <Text fontSize="xs" color="gray.500" mt={3}>
              Stai visualizzando solo gli interventi di oggi da iniziare o in corso. Cambia i filtri qui sopra per vedere gli altri giorni o gli interventi completati.
            </Text>
          )}
        </Box>

        {loading ? (
          <Box w="full" bg="white" borderRadius="12px" boxShadow={shadowBox} p={{ base: 2, md: 4 }}>
            <Center minH="300px">
              <Spinner color="#9FB50C" size="lg" />
            </Center>
          </Box>
        ) : tasks.length === 0 ? (
          <Box w="full" bg="white" borderRadius="12px" boxShadow={shadowBox} p={{ base: 2, md: 4 }}>
            <Center minH="200px">
              <Text color="gray.500">
                {isDefaultFilters ? 'Nessun intervento da iniziare o in corso per oggi' : 'Nessun intervento trovato con i filtri selezionati'}
              </Text>
            </Center>
          </Box>
        ) : isMobile ? (
          /* Mobile Card View */
          <Box w="full" px={2}>
            {tasks.map(task => (
              <EmployeeTasksCardComponent key={task.id} task={task} onTaskUpdate={fetchTasks} />
            ))}
          </Box>
        ) : (
          /* Desktop Table View */
          <Box w="full" bg="white" borderRadius="12px" boxShadow={shadowBox} p={{ base: 2, md: 4 }} overflowX="auto">
            <Table size="sm">
              <Thead>
                <Tr bg="gray.50">
                  <Th>Intervento</Th>
                  <Th>Cliente</Th>
                  <Th>Data e ora pianificata</Th>
                  <Th>Inizio effettivo</Th>
                  <Th>Fine effettiva</Th>
                  <Th>Stato</Th>
                  <Th>Rapporto d'Intervento</Th>
                </Tr>
              </Thead>
              <Tbody>
                {tasks.map(task => {
                  return (
                    <Tr key={task.id} _hover={{ bg: 'gray.50' }}>
                      <Td>
                        <VStack align="start" spacing={0}>
                          <Text fontWeight="bold" fontSize="sm">
                            {task.title}
                          </Text>
                          {task.address && (
                            <Link href={buildMapsUrl(task.address)} isExternal fontSize="xs" color="blue.500" textDecoration="underline">
                              {task.address}
                            </Link>
                          )}
                        </VStack>
                      </Td>
                      <Td fontSize="sm">{task.customer.fullName}</Td>
                      <Td fontSize="sm">
                        {formatDateDDmmYYYY(task.scheduledDateTime)}
                        <br />
                        {formatTime(task.scheduledDateTime)}
                      </Td>
                      <Td fontSize="sm">
                        {task.effectiveStartDateTime ? (
                          <>
                            {formatDateDDmmYYYY(task.effectiveStartDateTime)}
                            <br />
                            {formatTime(task.effectiveStartDateTime)}
                          </>
                        ) : (
                          '–'
                        )}
                      </Td>
                      <Td fontSize="sm">
                        {task.effectiveEndDateTime ? (
                          <>
                            {formatDateDDmmYYYY(task.effectiveEndDateTime)}
                            <br />
                            {formatTime(task.effectiveEndDateTime)}
                          </>
                        ) : (
                          '–'
                        )}
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={task.status === 'COMPLETATO' ? 'green' : task.status === 'IN_CORSO' ? 'orange' : 'blue'}
                          cursor={task.status === 'COMPLETATO' ? 'default' : 'pointer'}
                          onClick={() => handleStatusChange(task)}
                          _hover={task.status !== 'COMPLETATO' ? { opacity: 0.8 } : {}}
                        >
                          {TASK_STATUS_LABELS[task.status]}
                        </Badge>
                      </Td>
                      <Td>
                        {task.status === 'COMPLETATO' && task.hasInterventionReport && task.interventionReportId ? (
                          <Button
                            size="xs"
                            onClick={() => history.push(`/dipendente/rapporti-intervento/${task.interventionReportId}`)}
                            _hover={{ bg: 'gray.700' }} _active={{ bg: 'gray.600' }} _focus={{ boxShadow: 'none' }}
                            backgroundColor="#688CC8" color="white" height="26px" borderRadius="6px" fontSize="xs"
                          >
                            Vedi Rapporto d'Intervento
                          </Button>
                        ) : task.status !== 'DA_INIZIARE' ? (
                          // Covers the normal IN_CORSO case and a task stuck COMPLETATO-without-report
                          // from before the completion flow moved into the report submission itself.
                          <Button
                            size="xs"
                            onClick={() => history.push(`/dipendente/interventi/${task.id}/rapporto`)}
                            _hover={{ bg: 'gray.700' }} _active={{ bg: 'gray.600' }} _focus={{ boxShadow: 'none' }}
                            backgroundColor="#688CC8" color="white" height="26px" borderRadius="6px" fontSize="xs"
                          >
                            Completa Rapporto
                          </Button>
                        ) : (
                          <Text fontSize="sm" color="gray.400">–</Text>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        )}
      </VStack>
    </Box>
  );
}
